import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { map, of } from 'rxjs';
import { catchError, switchMap, withLatestFrom } from 'rxjs/operators';
import { DateService } from 'src/app/shared/services/date.service';

import {
  loadBlightData,
  loadBlightDataFailure,
  loadBlightDataIgnored,
  loadBlightDataSuccess,
  loadParticipatingCropFields,
  loadParticipatingCropFieldsFailure,
  loadParticipatingCropFieldsSuccess,
  resetBlightData,
} from './actions';
import { selectBlightData } from './selectors';
import { AppState } from '../../shared/models';
import { BlightData, Day } from '../models/models';
import { BlightService } from '../services/blight.service';
import { selectCropYear } from '../../store/selectors';

@Injectable()
export class BlightEffects {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly dateService = inject(DateService);
  private readonly blightService = inject(BlightService);

  readonly loadParticipatingCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadParticipatingCropFields),
      switchMap(({ payload: { cropYear } }) =>
        this.blightService.getParticipatingCropFields(cropYear).pipe(
          map((cropFields) => loadParticipatingCropFieldsSuccess({ cropFields })),
          catchError(() => of(loadParticipatingCropFieldsFailure())),
        ),
      ),
    ));

  readonly loadBlightData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBlightData),
      withLatestFrom(this.store.select(selectBlightData)),
      withLatestFrom(this.store.select(selectCropYear)),
      switchMap(([[{ payload: { from, to } }, currentBlightData], cropYear]) => {
        if (!this.hasMissingData(currentBlightData, from, to)) {
          return of(loadBlightDataIgnored());
        }
        return this.blightService.getBlightData(from, to, undefined, cropYear).pipe(
          map((newData) => loadBlightDataSuccess({ data: this.mergeBlightData(currentBlightData, newData) })),
          catchError(() => of(loadBlightDataFailure({ message: 'errors.blight.genericError' }))),
        );
      }),
    ));

  readonly resetBlightData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetBlightData),
      map(({ payload: { from, to } }) => loadBlightData({ from, to })),
    ),
  );

  private hasMissingData(blightData: BlightData[], from: Date, to: Date): boolean {
    if (!blightData?.length) {
      return true;
    }

    const dateRange = this.dateService.generateDaysBetweenDates(from, to);

    return blightData.some((data) => {
      const missingDates = dateRange.filter(
        (date) =>
          !data.blightResult?.days?.some((day) =>
            this.dateService.isDateSameDay(new Date(day?.date), new Date(date)),
          ),
      );
      return missingDates.length > 0;
    });
  }

  private mergeBlightData(oldData: BlightData[], newData: BlightData[]): BlightData[] {
    const mergedData = new Map<number, BlightData>();

    oldData.forEach((oldBlightData) => {
      mergedData.set(oldBlightData.cropFieldId, oldBlightData);
    });

    newData.forEach((newBlightData) => {
      const oldBlightData = mergedData.get(newBlightData.cropFieldId);

      if (oldBlightData) {
        mergedData.set(newBlightData.cropFieldId, {
          ...newBlightData,
          blightResult: {
            ...newBlightData.blightResult,
            days: oldBlightData?.blightResult
              ? this.mergeBlightDays(oldBlightData.blightResult?.days ?? [], newBlightData.blightResult?.days ?? [])
              : [],
          },
        });
      } else {
        mergedData.set(newBlightData.cropFieldId, newBlightData);
      }
    });

    return Array.from(mergedData.values());
  }

  private mergeBlightDays(oldDays: Day[], newDays: Day[]): Day[] {
    const mergedDays = new Map<Date, Day>();

    oldDays?.forEach((d) => {
      mergedDays.set(d.date, d);
    });

    newDays?.forEach((d) => {
      mergedDays.set(d.date, d);
    });

    return Array.from(mergedDays.values());
  }
}
